/**
 * Google Play Billing Service
 * Handles in-app purchases for Android devices using scgs-capacitor-subscribe plugin
 */

import { logger } from '../utils/logger';
import { Subscribe, type Product, type Purchase } from 'scgs-capacitor-subscribe';
import { Capacitor } from '@capacitor/core';

// Product SKUs for Google Play Console
const PRODUCT_SKUS = {
  monthly: 'ora_monthly_subscription',
  yearly: 'ora_yearly_subscription',
} as const;

export type GooglePlayProductSku = keyof typeof PRODUCT_SKUS;

// Use the plugin's types directly
export type GooglePlayProduct = Product;
export type GooglePlayPurchase = Purchase;

/**
 * Safely get the Google Play Billing plugin
 */
const getGooglePlayBillingPlugin = () => {
  return Subscribe;
};

/**
 * Check if Google Play Billing is available
 */
export const isGooglePlayBillingAvailable = async (): Promise<boolean> => {
  try {
    // Check if we're in a Capacitor environment
    if (!Capacitor.isNativePlatform()) {
      logger.debug('Google Play Billing not available: Not in Capacitor environment');
      return false;
    }

    // Test connection to Google Play Billing
    const plugin = getGooglePlayBillingPlugin();
    await plugin.initialize({
      enablePendingPurchases: true,
      enableLogging: true
    });
    logger.debug('Google Play Billing initialization test successful');

    return true;
  } catch (error) {
    logger.error('Error checking Google Play Billing availability', error);
    return false;
  }
};

/**
 * Initialize Google Play Billing connection
 */
export const initializeGooglePlayBilling = async (): Promise<boolean> => {
  try {
    const plugin = getGooglePlayBillingPlugin();
    await plugin.initialize({
      enablePendingPurchases: true,
      enableLogging: true
    });

    logger.debug('Google Play Billing initialized successfully');
    return true;
  } catch (error) {
    logger.error('Failed to initialize Google Play Billing', error);
    return false;
  }
};

/**
 * Get available products from Google Play Console
 */
export const getGooglePlayProducts = async (): Promise<GooglePlayProduct[]> => {
  try {
    if (!await isGooglePlayBillingAvailable()) {
      throw new Error('Google Play Billing not available');
    }

    const plugin = getGooglePlayBillingPlugin();

    const skuList = Object.values(PRODUCT_SKUS);
    const result = await plugin.getProducts({
      productIds: skuList
    });

    logger.debug('Google Play products retrieved', result);
    return result.products || [];
  } catch (error) {
    logger.error('Failed to get Google Play products', error);
    throw error;
  }
};

/**
 * Purchase a subscription product
 */
export const purchaseGooglePlayProduct = async (
  sku: GooglePlayProductSku,
  userId?: string
): Promise<GooglePlayPurchase> => {
  try {
    if (!await isGooglePlayBillingAvailable()) {
      throw new Error('Google Play Billing not available');
    }

    const plugin = getGooglePlayBillingPlugin();

    logger.debug('Starting Google Play purchase', { sku, userId });

    const result = await plugin.purchaseProduct({
      productId: PRODUCT_SKUS[sku],
      userId: userId || 'user_' + Date.now()
    });

    if (result.purchase) {
      logger.debug('Google Play purchase successful', result.purchase);

      // Acknowledge the purchase (required for subscriptions)
      await plugin.acknowledgePurchase({
        purchaseToken: result.purchase.purchaseToken
      });

      return result.purchase;
    } else {
      throw new Error('Purchase failed: No purchase data returned');
    }
  } catch (error) {
    logger.error('Google Play purchase failed', error);
    throw error;
  }
};

/**
 * Get existing purchases (for restoring subscriptions)
 */
export const getGooglePlayPurchases = async (): Promise<GooglePlayPurchase[]> => {
  try {
    if (!await isGooglePlayBillingAvailable()) {
      return [];
    }

    const plugin = getGooglePlayBillingPlugin();

    const result = await plugin.getActivePurchases({
      userId: 'user_' + Date.now()
    });

    logger.debug('Google Play purchases retrieved', result);
    return result.purchases || [];
  } catch (error) {
    logger.error('Failed to get Google Play purchases', error);
    return [];
  }
};

/**
 * Verify purchase on server (placeholder - implement with your backend)
 */
export const verifyGooglePlayPurchase = async (
  purchase: GooglePlayPurchase,
  userId?: string
): Promise<boolean> => {
  try {
    // This should call your backend API to verify the purchase with Google Play
    const response = await fetch('/.netlify/functions/verify-google-play-purchase', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        purchase,
        userId,
        timestamp: new Date().toISOString()
      }),
    });

    if (!response.ok) {
      throw new Error(`Verification failed: ${response.status}`);
    }

    const result = await response.json();
    logger.debug('Google Play purchase verification result', result);

    return result.valid === true;
  } catch (error) {
    logger.error('Google Play purchase verification failed', error);
    return false;
  }
};

/**
 * Handle purchase completion (call after successful verification)
 */
export const completeGooglePlayPurchase = async (
  purchase: GooglePlayPurchase,
  userId?: string
): Promise<void> => {
  try {
    // Verify the purchase first
    const isValid = await verifyGooglePlayPurchase(purchase, userId);

    if (!isValid) {
      throw new Error('Purchase verification failed');
    }

    // Update user subscription status via backend
    const response = await fetch('/.netlify/functions/complete-google-play-purchase', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        purchase,
        userId,
        timestamp: new Date().toISOString()
      }),
    });

    if (!response.ok) {
      throw new Error(`Purchase completion failed: ${response.status}`);
    }

    logger.debug('Google Play purchase completed successfully');
  } catch (error) {
    logger.error('Failed to complete Google Play purchase', error);
    throw error;
  }
};

/**
 * Demo mode functions for testing
 */
export const isGooglePlayDemoMode = (): boolean => {
  return localStorage.getItem('google-play-demo-mode') === 'true' ||
         process.env.NODE_ENV === 'development';
};

export const enableGooglePlayDemoMode = (): void => {
  localStorage.setItem('google-play-demo-mode', 'true');
  logger.debug('Google Play demo mode enabled');
};

export const disableGooglePlayDemoMode = (): void => {
  localStorage.removeItem('google-play-demo-mode');
  logger.debug('Google Play demo mode disabled');
};

/**
 * Demo purchase for testing
 */
export const createDemoGooglePlayPurchase = async (
  sku: GooglePlayProductSku,
  userId?: string
): Promise<boolean> => {
  logger.debug('Demo Google Play purchase', { sku, userId });

  const productNames = {
    monthly: 'Monthly Premium Subscription',
    yearly: 'Annual Premium Subscription (Save 30%)'
  };

  const shouldSimulateSuccess = window.confirm(
    `Demo Mode: Google Play Billing\n\nThis would purchase: ${productNames[sku]}\n\nClick OK to simulate successful purchase, Cancel to simulate failure.`
  );

  if (shouldSimulateSuccess) {
    logger.debug('Demo Google Play purchase successful');
    return true;
  } else {
    throw new Error('Demo: Google Play purchase cancelled by user');
  }
};

// Type declarations handled in platform.ts